// Lead Qualifier Questions - Exact match to Fillout screenshots

export interface QuestionOption {
  value: string;
  label: { he: string; en: string };
}

export interface Question {
  id: string;
  questionNumber: string;
  text: { he: string; en: string };
  type: 'multi-select' | 'single-select' | 'text';
  options?: QuestionOption[];
  required: boolean;
  condition?: (answers: Record<string, any>) => boolean;
  hasOther?: boolean;
}

export const questions: Question[] = [
  // Question 1 - Multi-select challenges
  {
    id: 'q1_challenges',
    questionNumber: '1',
    text: {
      he: 'מה האתגר הכי גדול שלך כרגע בעסק? (אפשר לבחור יותר מתשובה אחת)',
      en: 'What is the biggest challenge you are currently facing in your business? (You can choose more than one answer)'
    },
    type: 'multi-select',
    required: true,
    hasOther: true,
    options: [
      { value: 'hard_to_grow_revenue', label: { he: 'קשה להגדיל הכנסות', en: 'Hard to grow revenue' } },
      { value: 'everything_depends_on_me', label: { he: 'תחושה שהכל תלוי בי', en: 'Feel like everything depends on me' } },
      { value: 'difficult_with_recruitment', label: { he: 'קשה לגייס עובדים', en: 'Difficult with recruitment' } },
      { value: 'operational_chaos', label: { he: 'אני כל היום מכבה שריפות', en: 'I\'m putting out fires all day' } },
      { value: 'workers_motivation', label: { he: 'קשה לשמור על מוטיבציה של עובדים', en: 'Hard to keep employees motivated' } },
      { value: 'non_business_taking_attention', label: { he: 'דברים מחוץ לעסק גונבים לי את תשומת הלב', en: 'Non-business things are stealing my attention' } },
      { value: 'i_have_stress_in_life', label: { he: 'יש לי לא מעט לחץ בחיים', en: 'I have quite a bit of stress in life' } },
    ]
  },

  // Question 2 - How long trying to solve
  {
    id: 'q2_duration',
    questionNumber: '2',
    text: {
      he: 'כמה זמן זה כבר מלווה אותך?',
      en: 'How long have you been dealing with this?'
    },
    type: 'single-select',
    required: true,
    options: [
      { value: 'recently', label: { he: 'רק לאחרונה התחיל להציק לי', en: 'Only recently started bothering me' } },
      { value: 'half_year', label: { he: 'כחצי שנה', en: 'About half a year' } },
      { value: 'over_year', label: { he: 'יותר משנה', en: 'More than a year' } },
    ]
  },

  // Question 3 - What have you tried
  {
    id: 'q3_tried',
    questionNumber: '3',
    text: {
      he: 'מה ניסית עד עכשיו כדי לטפל בזה? (אפשר לבחור יותר מתשובה אחת)',
      en: 'What have you tried so far to address this? (You can choose more than one answer)'
    },
    type: 'multi-select',
    required: true,
    hasOther: true,
    options: [
      { value: 'read_books', label: { he: 'קראתי ספרים ומאמרים', en: 'Read books and articles' } },
      { value: 'listened_to_webinars', label: { he: 'הקשבתי לוובינרים', en: 'Listened to webinars' } },
      { value: 'consultant', label: { he: 'עבדתי עם יועץ', en: 'Worked with a consultant' } },
      { value: 'invested_more_time_in_work', label: { he: 'השקעתי יותר זמן בעבודה', en: 'Invested more time in work' } },
      { value: 'didnt_do_anything_in_practice', label: { he: 'לא עשיתי שום דבר בפועל', en: 'Didn\'t do anything in practice.' } },
    ]
  },

  // Question 4 - Conditional: Why hasn't it worked (if over half year)
  {
    id: 'q4_why_not_worked',
    questionNumber: '4',
    text: {
      he: 'אם הניסיון לפתור את זה נמשך כבר מעל חצי שנה, מה לדעתך הסיבה שזה לא השתנה עד היום?',
      en: 'If you\'ve been trying to solve this for over half a year, what do you think is the reason it hasn\'t changed until today?'
    },
    type: 'text',
    required: false,
    condition: (answers) => answers.q2_duration === 'over_year' || answers.q2_duration === 'half_year'
  },

  // Question 5 - Conditional: Why act now (if recently)
  {
    id: 'q5_why_now',
    questionNumber: '5',
    text: {
      he: 'ואם רק עכשיו מתחילים להתעסק בזה, מה גרם לפעול דווקא עכשיו?',
      en: 'And if you\'re just now starting to deal with this, what\'s causing you to act specifically now?'
    },
    type: 'text',
    required: false,
    condition: (answers) => answers.q2_duration === 'recently'
  },

  // Question 6 - Urgency
  {
    id: 'q6_urgency',
    questionNumber: '6',
    text: {
      he: 'עד כמה דחוף לך לטפל בזה?',
      en: 'How urgent is it for you to handle this?'
    },
    type: 'single-select',
    required: true,
    options: [
      { value: 'not_urgent', label: { he: 'לא כזה דחוף, אין לחץ', en: 'Not that urgent, no pressure' } },
      { value: 'near_future', label: { he: 'דחוף בטווח קצר, אני רוצה לטפל בזה בקרוב', en: 'Urgent in the near future, I want to handle this soon' } },
      { value: 'very_urgent', label: { he: 'מאוד דחוף, אני רוצה לטפל בזה בהקדם האפשרי', en: 'Very urgent, I want to handle this as soon as possible' } },
    ]
  },

  // Question 7a - Business field
  {
    id: 'q7a_business_field',
    questionNumber: '7א',
    text: {
      he: 'באיזה תחום העסק שלך?',
      en: 'What field is your business in?'
    },
    type: 'text',
    required: true
  },

  // Question 7b - Team size
  {
    id: 'q7b_team_size',
    questionNumber: '7ב',
    text: {
      he: 'כמה עובדים יש לך בצוות?',
      en: 'How many employees are in your team?'
    },
    type: 'single-select',
    required: true,
    options: [
      { value: 'alone', label: { he: 'לבד', en: 'Alone' } },
      { value: 'small_team', label: { he: 'יש לי צוות של מספר עובדים', en: 'I have a team of several employees' } },
      { value: 'large_team', label: { he: 'יש לי צוות של מעל 10 עובדים', en: 'I have a team of more than 10 employees' } },
    ]
  },

  // Question 8 - Most important improvement
  {
    id: 'q8_important',
    questionNumber: '8',
    text: {
      he: 'כשחושבים על שיפור העסק, מה הכי חשוב?',
      en: 'When you think about improving the business, what\'s most important to you?'
    },
    type: 'single-select',
    required: true,
    options: [
      { value: 'earn_more', label: { he: 'להרוויח יותר', en: 'Earn more' } },
      { value: 'reduce_burden', label: { he: 'להוריד ממני עומס', en: 'Reduce burden from me' } },
      { value: 'business_potential_fulfillment_feeling', label: { he: 'להרגיש שאני ממצה את הפוטנציאל של העסק שלי', en: 'Feel like I\'m fulfilling the potential of my business' } },
    ]
  },

  // Question 8a - Potential monthly revenue (conditional on earn_more)
  {
    id: 'q8a_potential_revenue',
    questionNumber: '8א',
    text: {
      he: 'אם הכל היה זורם חלק בעסק, מה פוטנציאל ההכנסה החודשי הצפוי?',
      en: 'If everything flowed smoothly in your business, what\'s the monthly income you expect?'
    },
    type: 'single-select',
    required: false,
    condition: (answers) => answers.q8_important === 'business_potential_fulfillment_feeling',
    options: [
      { value: 'up_to_100k', label: { he: 'עד 100 אלף ש"ח בחודש', en: 'Up to 100K NIS/month' } },
      { value: 'up_to_1m', label: { he: 'עד מיליון ש"ח בחודש', en: 'Up to 1M NIS/month' } },
      { value: 'up_to_10m', label: { he: 'עד 10 מיליון ש"ח בחודש', en: 'Up to 10M NIS/month' } },
      { value: 'over_10m', label: { he: 'מעל 10 מיליון ש"ח בחודש', en: 'Over 10M NIS/month' } },
    ]
  },

  // Question 8b - Current monthly revenue
  {
    id: 'q8b_current_revenue',
    questionNumber: '8ב',
    text: {
      he: 'באיזה סדר גודל ההכנסות של העסק כיום?',
      en: 'What\'s the current size of your business income?'
    },
    type: 'single-select',
    required: true,
    options: [
      { value: 'up_to_100k', label: { he: 'עד 100 אלף ש"ח בחודש', en: 'Up to 100K NIS/month' } },
      { value: 'up_to_1m', label: { he: 'עד מיליון ש"ח בחודש', en: 'Up to 1M NIS/month' } },
      { value: 'up_to_10m', label: { he: 'עד 10 מיליון ש"ח בחודש', en: 'Up to 10M NIS/month' } },
      { value: 'over_10m', label: { he: 'מעל 10 מיליון ש"ח בחודש', en: 'Over 10M NIS/month' } },
    ]
  },

  // Question 9 - Previous consulting experience
  {
    id: 'q9_consulting',
    questionNumber: '9',
    text: {
      he: 'האם אי פעם ניסית ייעוץ עסקי?',
      en: 'Have you ever tried business consulting?'
    },
    type: 'single-select',
    required: true,
    options: [
      { value: 'no_alone', label: { he: 'לא, ניסיתי לפתור את זה לבד', en: 'No, tried to solve this alone' } },
      { value: 'yes_not_helpful', label: { he: 'כן, אבל לא ללא הצלחה', en: 'Yes, but didn\'t succeed' } },
      { value: 'yes_helpful', label: { he: 'כן, ועזר לי מאוד', en: 'Yes, and it helped me a lot' } },
    ]
  },

  // Question 10 - Expectations from consultant
  {
    id: 'q10_expectations',
    questionNumber: '10',
    text: {
      he: 'מה היית מצפה לקבל מיועץ עסקי כדי שזה באמת יעבוד לך? (אפשר לבחור יותר מתשובה אחת)',
      en: 'What would you expect to get from a business consultant to really work for you? (You can choose more than one answer)'
    },
    type: 'multi-select',
    required: true,
    options: [
      { value: 'experience_similar', label: { he: 'ניסיון בפתרון בעיות דומות לשלי', en: 'Experience in solving problems similar to mine' } },
      { value: 'action_plan', label: { he: 'תוכנית פעולה מפורטת עם יעדים מדידים', en: 'Detailed action plan with measurable goals' } },
      { value: 'custom_solutions', label: { he: 'פתרונות מותאמים אישית לעסק שלי', en: 'Solutions customized personally for my business' } },
      { value: 'practical_experience', label: { he: 'שיתוף ניסיון מעשי מעסקים דומים', en: 'Sharing practical experience from similar businesses' } },
      { value: 'knowledge_transfer', label: { he: 'העברת ידע כדי שאוכל להמשיך לבד', en: 'Knowledge transfer so I can continue alone' } },
      { value: 'reduce_dependence', label: { he: 'הורדת התלות של העסק בי אישית', en: 'Reducing the business\'s dependence on me personally' } },
      { value: 'leadership_skills', label: { he: 'פיתוח כישורי ניהול, מנהיגות ותקשורת', en: 'Developing management, leadership and communication skills' } },
      { value: 'work_life_balance', label: { he: 'איזון בין חיי העבודה לחיים האישיים', en: 'Work-life balance' } },
    ]
  },

  // Question 11 - Willingness for deep analysis
  {
    id: 'q11_deep_analysis',
    questionNumber: '11',
    text: {
      he: 'אם נוכל להראות לך דרך וודאית לפתרון, שמוכיחה את עצמה אצל עסקים דומים לשלך, יש עניין למלא שאלון של 100 שאלות שעוזר לאבחן את מקור הבעיות?',
      en: 'If we could show you a certain path to solution, proven with similar businesses to yours, would you want to fill out a 100-question questionnaire that helps identify the source of the problems you\'re experiencing?'
    },
    type: 'single-select',
    required: true,
    options: [
      { value: 'yes', label: { he: 'כן, רוצה לגלות את האמת על העסק שלי.', en: 'Yes, I want to discover the truth about my business.' } },
      { value: 'maybe', label: { he: 'אולי, אפשר לנסות.', en: 'Maybe, could try.' } },
      { value: 'no', label: { he: 'לא, זה פחות מתאים לי בשלב הזה.', en: 'No, it\'s less suitable for me at this stage.' } },
    ]
  },
];

// Translations for UI elements
export const translations = {
  he: {
    // Landing page
    landingTitle: '"רק אני יכול לעשות את זה נכון" - מכירים? זו בדיוק הבעיה',
    landingSubtitle: 'מלאו את השאלון וקבלו גישה מיידית למדריך',
    timerText: 'השאלון ייקח לך 2-3 דקות',
    tipText: 'חשוב לענות לפי מה שמרגיש נכון באמת - זו ההזדמנות להיות בכנות מול עצמך',
    startButton: 'מתחילים!',

    // Progress steps
    stepStart: 'התחלה',
    stepQuestions: 'שאלות',
    stepFinish: 'סיום',

    // Navigation
    nextButton: 'הבא',
    backButton: 'חזור',

    // Contact form
    contactTitle: 'כמעט סיימנו...',
    firstName: 'שם פרטי',
    lastName: 'שם משפחה',
    email: 'מייל',
    phone: 'טלפון נייד',
    privacyConsent: 'אני מאשר/ת קבלת חומרים שיווקיים ופניות מכירתיות בהתאם למדיניות הפרטיות. ניתן לבטל בכל עת.',
    privacyPolicy: 'למדיניות הפרטיות',

    // Completion
    thankYouTitle: 'תודה רבה!',
    thankYouSubtitle: 'קיבלנו את הפרטים שלך. נחזור אליך בהקדם.',
    start360Button: 'התחל ניתוח מעמיק של 100 שאלות',
    finishButton: 'סיים',

    // Other
    other: 'Other',
    required: '*',
  },
  en: {
    // Landing page
    landingTitle: 'Time to discover what\'s really blocking you, and get an immediate guide to breakthrough',
    landingSubtitle: 'Fill out the questionnaire and get immediate access to the guide',
    timerText: 'The questionnaire will take you 2-3 minutes',
    tipText: 'It\'s important to answer how the situation really feels to you because this is your opportunity to be honest with yourself first',
    startButton: 'Let\'s start!',

    // Progress steps
    stepStart: 'Start',
    stepQuestions: 'Questions',
    stepFinish: 'Finish',

    // Navigation
    nextButton: 'Next',
    backButton: 'Back',

    // Contact form
    contactTitle: 'Almost done...',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    phone: 'Mobile Phone',
    privacyConsent: 'I agree to receive marketing materials and sales communications according to the privacy policy. Unsubscribe anytime.',
    privacyPolicy: 'Privacy Policy',

    // Completion
    thankYouTitle: 'Thank you!',
    thankYouSubtitle: 'We received your details. We\'ll get back to you soon.',
    start360Button: 'Start deep 100-question analysis',
    finishButton: 'Finish',

    // Other
    other: 'Other',
    required: '*',
  }
};

export type Language = 'he' | 'en';
